import type { AudioEngine } from './engine';

/**
 * Every sound in the game, synthesized from nothing. Each recipe builds a
 * small one-shot node graph into the engine's sfx bus (some also feed the
 * shared reverb). All parameters were tuned by ear on the ?soundboard.
 */

type Rng = () => number;

function noiseBuffer(ctx: AudioContext, seconds: number, rng: Rng, brown = false): AudioBuffer {
  const buffer = ctx.createBuffer(1, Math.max(1, Math.floor(seconds * ctx.sampleRate)), ctx.sampleRate);
  const data = buffer.getChannelData(0);
  let last = 0;
  for (let index = 0; index < data.length; index += 1) {
    const white = rng() * 2 - 1;
    if (brown) {
      last = (last + 0.02 * white) / 1.02;
      data[index] = last * 3.5;
    } else {
      data[index] = white;
    }
  }
  return buffer;
}

function envGain(ctx: AudioContext, at: number, peak: number, decaySeconds: number, attackSeconds = 0.002): GainNode {
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.0001, at);
  gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, peak), at + attackSeconds);
  gain.gain.exponentialRampToValueAtTime(0.0001, at + attackSeconds + decaySeconds);
  return gain;
}

function tone(
  engine: AudioEngine,
  opts: {
    type?: OscillatorType;
    freq: number;
    freqEnd?: number;
    glideSeconds?: number;
    peak: number;
    decay: number;
    attack?: number;
    at?: number;
    dest?: AudioNode;
    pan?: number;
    reverb?: number;
  },
): void {
  const ctx = engine.ctx!;
  const at = opts.at ?? ctx.currentTime;
  const osc = ctx.createOscillator();
  osc.type = opts.type ?? 'sine';
  osc.frequency.setValueAtTime(opts.freq, at);
  if (opts.freqEnd !== undefined) {
    osc.frequency.exponentialRampToValueAtTime(Math.max(1, opts.freqEnd), at + (opts.glideSeconds ?? opts.decay));
  }
  const gain = envGain(ctx, at, opts.peak, opts.decay, opts.attack);
  let tail: AudioNode = gain;
  if (opts.pan !== undefined) {
    const panner = ctx.createStereoPanner();
    panner.pan.value = opts.pan;
    gain.connect(panner);
    tail = panner;
  }
  osc.connect(gain);
  tail.connect(opts.dest ?? engine.sfx);
  if (opts.reverb) {
    const send = ctx.createGain();
    send.gain.value = opts.reverb;
    tail.connect(send);
    send.connect(engine.reverbSend);
  }
  osc.start(at);
  osc.stop(at + (opts.attack ?? 0.002) + opts.decay + 0.05);
  engine.countVoice(osc);
}

function noiseHit(
  engine: AudioEngine,
  opts: {
    seconds: number;
    peak: number;
    filter?: { type: BiquadFilterType; freq: number; freqEnd?: number; q?: number };
    at?: number;
    pan?: number;
    brown?: boolean;
    reverb?: number;
    rng: Rng;
  },
): void {
  const ctx = engine.ctx!;
  const at = opts.at ?? ctx.currentTime;
  const source = ctx.createBufferSource();
  source.buffer = noiseBuffer(ctx, opts.seconds + 0.05, opts.rng, opts.brown);
  const gain = envGain(ctx, at, opts.peak, opts.seconds);
  let head: AudioNode = source;
  if (opts.filter) {
    const filter = ctx.createBiquadFilter();
    filter.type = opts.filter.type;
    filter.frequency.setValueAtTime(opts.filter.freq, at);
    if (opts.filter.freqEnd !== undefined) {
      filter.frequency.exponentialRampToValueAtTime(Math.max(20, opts.filter.freqEnd), at + opts.seconds);
    }
    filter.Q.value = opts.filter.q ?? 1;
    source.connect(filter);
    head = filter;
  }
  let tail: AudioNode = gain;
  if (opts.pan !== undefined) {
    const panner = ctx.createStereoPanner();
    panner.pan.value = opts.pan;
    gain.connect(panner);
    tail = panner;
  }
  head.connect(gain);
  tail.connect(engine.sfx);
  if (opts.reverb) {
    const send = ctx.createGain();
    send.gain.value = opts.reverb;
    tail.connect(send);
    send.connect(engine.reverbSend);
  }
  source.start(at);
  source.stop(at + opts.seconds + 0.05);
  engine.countVoice(source);
}

/** A short inharmonic bell: partial stack with per-partial decays. */
function bell(
  engine: AudioEngine,
  fundamental: number,
  ratios: number[],
  decays: number[],
  peak: number,
  at?: number,
  reverb = 0.25,
): void {
  ratios.forEach((ratio, index) => {
    tone(engine, {
      freq: fundamental * ratio,
      peak: peak / (index + 1.4),
      decay: decays[index] ?? 0.3,
      at,
      reverb: index === 0 ? reverb : 0,
    });
  });
}

// ---- the palette ---------------------------------------------------------

export const SFX: Record<string, (engine: AudioEngine, rng: Rng) => void> = {
  uiTick(engine, rng) {
    noiseHit(engine, { seconds: 0.02, peak: 0.1, filter: { type: 'highpass', freq: 2000 }, rng });
  },

  clink(engine, rng) {
    // v2: a dry pick-tap on stone — one very short high tick and a breath of
    // gravel. No ringing metal partials (the old one read as glassy).
    const pan = (rng() - 0.5) * 1.0;
    noiseHit(engine, {
      seconds: 0.012,
      peak: 0.16,
      filter: { type: 'highpass', freq: 3800 + rng() * 1200 },
      pan,
      rng,
    });
    noiseHit(engine, {
      seconds: 0.05,
      peak: 0.07,
      filter: { type: 'bandpass', freq: 900 + rng() * 300, q: 2 },
      pan,
      rng,
    });
  },

  oreDrop(engine, rng) {
    const ctx = engine.ctx!;
    for (const offset of [0, 0.055, 0.115].slice(0, 2 + Math.floor(rng() * 2))) {
      tone(engine, { freq: 90, freqEnd: 50, peak: 0.2, decay: 0.06, at: ctx.currentTime + offset });
    }
    noiseHit(engine, { seconds: 0.18, peak: 0.1, filter: { type: 'bandpass', freq: 600, q: 1.5 }, rng });
  },

  placeThunk(engine, rng) {
    tone(engine, { freq: 120, freqEnd: 60, glideSeconds: 0.1, peak: 0.5, decay: 0.16 });
    noiseHit(engine, { seconds: 0.06, peak: 0.18, filter: { type: 'lowpass', freq: 400 }, rng });
  },

  demolishCrumble(engine, rng) {
    noiseHit(engine, {
      seconds: 0.45,
      peak: 0.45,
      brown: true,
      filter: { type: 'lowpass', freq: 1400, freqEnd: 180 },
      rng,
    });
    const ctx = engine.ctx!;
    for (const offset of [0.12 + rng() * 0.04, 0.26 + rng() * 0.04]) {
      tone(engine, { freq: 80, freqEnd: 55, peak: 0.18, decay: 0.08, at: ctx.currentTime + offset });
    }
  },

  truckOut(engine, rng) {
    // v2: a chugging motor receding — amplitude-pulsed low noise, no
    // sawtooth buzz. Pulse rate rises slightly as it pulls away.
    const ctx = engine.ctx!;
    const at = ctx.currentTime;
    const source = ctx.createBufferSource();
    source.buffer = noiseBuffer(ctx, 1.8, rng, true);
    const lowpass = ctx.createBiquadFilter();
    lowpass.type = 'lowpass';
    lowpass.frequency.setValueAtTime(420, at);
    lowpass.frequency.linearRampToValueAtTime(160, at + 1.7);
    const chug = ctx.createGain();
    chug.gain.value = 0.6;
    const chugLfo = ctx.createOscillator();
    chugLfo.type = 'square';
    chugLfo.frequency.setValueAtTime(9, at);
    chugLfo.frequency.linearRampToValueAtTime(13, at + 1.6);
    const chugDepth = ctx.createGain();
    chugDepth.gain.value = 0.35;
    chugLfo.connect(chugDepth);
    chugDepth.connect(chug.gain);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(0.3, at + 0.12);
    gain.gain.setValueAtTime(0.3, at + 0.9);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + 1.8);
    source.connect(lowpass);
    lowpass.connect(chug);
    chug.connect(gain);
    gain.connect(engine.sfx);
    source.start(at);
    source.stop(at + 1.85);
    chugLfo.start(at);
    chugLfo.stop(at + 1.85);
    engine.countVoice(source);
  },

  saleChaChing(engine, rng) {
    // v2: the shopkeeper's counter bell — one warm ding-ding (fundamental +
    // soft octave), a drawer roll underneath. Gentler than the old FM bells.
    const ctx = engine.ctx!;
    const at = ctx.currentTime;
    noiseHit(engine, { seconds: 0.12, peak: 0.08, filter: { type: 'bandpass', freq: 700, q: 1.2 }, rng });
    for (const [offset, peak] of [[0.1, 0.2], [0.28, 0.16]] as const) {
      tone(engine, { freq: 987.77, peak, decay: 0.6, at: at + offset, reverb: 0.15 });
      tone(engine, { freq: 987.77 * 2.02, peak: peak * 0.35, decay: 0.4, at: at + offset });
    }
  },

  coinSpill(engine, rng) {
    // v2: three or four heavy coins on wood — lower, rounder, with a dull
    // tabletop knock under the first. Less wind-chime, more money.
    const ctx = engine.ctx!;
    const at = ctx.currentTime;
    tone(engine, { freq: 140, freqEnd: 90, peak: 0.15, decay: 0.09 });
    const count = 3 + Math.floor(rng() * 2);
    for (let index = 0; index < count; index += 1) {
      const offset = 0.05 + index * (0.08 + rng() * 0.05);
      const f = 1100 + rng() * 700;
      tone(engine, { type: 'triangle', freq: f, peak: 0.11, decay: 0.12, at: at + offset, pan: (rng() - 0.5) * 0.6 });
      tone(engine, { type: 'triangle', freq: f * 1.42, peak: 0.05, decay: 0.08, at: at + offset });
    }
  },

  payrollThud(engine, rng) {
    // v2: the ledger slapped shut — deep book thump plus a paper flap.
    const ctx = engine.ctx!;
    tone(engine, { freq: 90, freqEnd: 45, glideSeconds: 0.12, peak: 0.35, decay: 0.22 });
    noiseHit(engine, {
      seconds: 0.09,
      peak: 0.1,
      filter: { type: 'bandpass', freq: 1600, q: 1 },
      at: ctx.currentTime + 0.02,
      rng,
    });
  },

  paperSlide(engine, rng) {
    noiseHit(engine, {
      seconds: 0.22,
      peak: 0.15,
      filter: { type: 'bandpass', freq: 900, freqEnd: 2000, q: 2 },
      rng,
    });
  },

  stamp(engine, rng) {
    const ctx = engine.ctx!;
    tone(engine, { freq: 84, freqEnd: 42, glideSeconds: 0.1, peak: 0.4, decay: 0.15 });
    noiseHit(engine, {
      seconds: 0.015,
      peak: 0.2,
      filter: { type: 'highpass', freq: 1200 },
      at: ctx.currentTime + 0.03,
      rng,
    });
  },

  busArrival(engine, rng) {
    // v2: the bus rolls up and sighs — slowing chug, air-brake hiss, and a
    // little door squeak. No sawtooth drone.
    const ctx = engine.ctx!;
    const at = ctx.currentTime;
    const source = ctx.createBufferSource();
    source.buffer = noiseBuffer(ctx, 1.4, rng, true);
    const lowpass = ctx.createBiquadFilter();
    lowpass.type = 'lowpass';
    lowpass.frequency.setValueAtTime(350, at);
    lowpass.frequency.linearRampToValueAtTime(120, at + 1.2);
    const chug = ctx.createGain();
    chug.gain.value = 0.6;
    const chugLfo = ctx.createOscillator();
    chugLfo.type = 'square';
    chugLfo.frequency.setValueAtTime(11, at);
    chugLfo.frequency.linearRampToValueAtTime(5, at + 1.2);
    const chugDepth = ctx.createGain();
    chugDepth.gain.value = 0.35;
    chugLfo.connect(chugDepth);
    chugDepth.connect(chug.gain);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(0.26, at + 0.15);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + 1.35);
    source.connect(lowpass);
    lowpass.connect(chug);
    chug.connect(gain);
    gain.connect(engine.sfx);
    source.start(at);
    source.stop(at + 1.4);
    chugLfo.start(at);
    chugLfo.stop(at + 1.4);
    engine.countVoice(source);
    noiseHit(engine, { seconds: 0.25, peak: 0.1, filter: { type: 'highpass', freq: 3500 }, at: at + 1.05, rng });
    tone(engine, { freq: 1300, freqEnd: 1650, glideSeconds: 0.12, peak: 0.04, decay: 0.14, at: at + 1.2 });
  },

  trainWhistle(engine, rng) {
    const ctx = engine.ctx!;
    const at = ctx.currentTime;
    for (const [freq, peak] of [
      [329.63, 0.18],
      [392.0, 0.15],
    ] as const) {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      const detune = 1 + (rng() - 0.5) * 0.005;
      osc.frequency.setValueAtTime(freq * detune * 0.966, at);
      osc.frequency.exponentialRampToValueAtTime(freq * detune, at + 0.15);
      osc.frequency.setValueAtTime(freq * detune, at + 1.05);
      osc.frequency.exponentialRampToValueAtTime(freq * detune * 0.891, at + 1.45);
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.0001, at);
      gain.gain.exponentialRampToValueAtTime(peak, at + 0.15);
      gain.gain.setValueAtTime(peak, at + 1.05);
      gain.gain.exponentialRampToValueAtTime(0.0001, at + 1.5);
      const send = ctx.createGain();
      send.gain.value = 0.5;
      osc.connect(gain);
      gain.connect(engine.sfx);
      gain.connect(send);
      send.connect(engine.reverbSend);
      osc.start(at);
      osc.stop(at + 1.6);
      engine.countVoice(osc);
    }
    // Steam breath under the tone.
    noiseHit(engine, { seconds: 1.2, peak: 0.05, filter: { type: 'bandpass', freq: 700, q: 0.8 }, rng });
  },

  schoolBell(engine, rng) {
    // v2: a teacher's handbell — bright fundamental with a gentle beat
    // against a slightly detuned twin, rung three times, quick and dry.
    const ctx = engine.ctx!;
    for (const offset of [0, 0.22, 0.44]) {
      const at = ctx.currentTime + offset;
      tone(engine, { freq: 1244.5, peak: 0.14, decay: 0.35, at, reverb: 0.1 });
      tone(engine, { freq: 1244.5 * 1.006, peak: 0.1, decay: 0.3, at });
      tone(engine, { freq: 1244.5 * 2.4, peak: 0.04, decay: 0.12, at });
    }
    void rng;
  },

  chapelBell(engine, rng) {
    // v2: one warm, unhurried toll — hum tone plus a mellow prime with a
    // soft attack, long tail, lots of canyon. Far less clangy.
    const ctx = engine.ctx!;
    const at = ctx.currentTime;
    tone(engine, { freq: 164.81, peak: 0.22, decay: 2.8, attack: 0.02, at, reverb: 0.5 });
    tone(engine, { freq: 329.63, peak: 0.1, decay: 2.0, attack: 0.015, at: at + 0.01, reverb: 0.3 });
    tone(engine, { freq: 494.0, peak: 0.04, decay: 0.9, attack: 0.01, at: at + 0.01 });
    void rng;
  },

  pressSting(engine, rng) {
    // v2: two heavy press clunks and the page zipping off the drum —
    // half the length, twice the punch, no musical tail.
    const ctx = engine.ctx!;
    const at = ctx.currentTime;
    for (const offset of [0, 0.16]) {
      tone(engine, { freq: 110, freqEnd: 60, glideSeconds: 0.06, peak: 0.32, decay: 0.1, at: at + offset });
      noiseHit(engine, { seconds: 0.05, peak: 0.14, filter: { type: 'lowpass', freq: 700 }, at: at + offset, rng });
    }
    noiseHit(engine, {
      seconds: 0.3,
      peak: 0.14,
      filter: { type: 'bandpass', freq: 1200, freqEnd: 3200, q: 1.5 },
      at: at + 0.34,
      rng,
    });
  },

  deny(engine, rng) {
    tone(engine, { type: 'square', freq: 140, freqEnd: 110, peak: 0.07, decay: 0.09 });
    void rng;
  },
};

export const SFX_NAMES = Object.keys(SFX);

/** The always-on desert: a very quiet brown-noise wind with slow gusts. */
export function startWindBed(engine: AudioEngine, rng: Rng): () => void {
  const ctx = engine.ctx!;
  const source = ctx.createBufferSource();
  source.buffer = noiseBuffer(ctx, 6, rng, true);
  source.loop = true;
  const filter = ctx.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.value = 380;
  filter.Q.value = 0.6;
  const sweep = ctx.createOscillator();
  sweep.frequency.value = 0.06;
  const sweepGain = ctx.createGain();
  sweepGain.gain.value = 220;
  sweep.connect(sweepGain);
  sweepGain.connect(filter.frequency);
  const gain = ctx.createGain();
  gain.gain.value = 0.045;
  const gustA = ctx.createOscillator();
  gustA.frequency.value = 0.09;
  const gustB = ctx.createOscillator();
  gustB.frequency.value = 0.013;
  const gustGainA = ctx.createGain();
  gustGainA.gain.value = 0.012;
  const gustGainB = ctx.createGain();
  gustGainB.gain.value = 0.008;
  gustA.connect(gustGainA);
  gustB.connect(gustGainB);
  gustGainA.connect(gain.gain);
  gustGainB.connect(gain.gain);
  source.connect(filter);
  filter.connect(gain);
  gain.connect(engine.sfx);
  source.start();
  sweep.start();
  gustA.start();
  gustB.start();
  return () => {
    for (const node of [source, sweep, gustA, gustB]) {
      try {
        node.stop();
      } catch {
        // already stopped
      }
    }
  };
}
